// Free-draw and pen-tool handlers for the canvas.
// Returns props bound to the active drawing tool.
import { useCallback, useRef, useState, useEffect } from "react";
import { nanoIdLike } from "./utils";
import { arrowHeadPath } from "./lineEdit";

const SIMPLIFY_THRESHOLD = 2; // px, prevent duplicate points

// Default "re-draw" animation auto-attached to freshly drawn paths so the
// stroke replays from start to end. Users can tweak/remove it in the inspector.
function drawAnimation() {
  return {
    type: "draw",
    duration: 1.4,
    delay: 0,
    easing: "ease-out",
    repeat: "1",
    direction: "normal",
    fillMode: "both",
    origin: "center",
  };
}

// Build a smoothed SVG path (quadratic Catmull-Rom approximation) from raw points.
export function smoothPath(points) {
  if (!points.length) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length - 1; i++) {
    const cx = (points[i].x + points[i + 1].x) / 2;
    const cy = (points[i].y + points[i + 1].y) / 2;
    d += ` Q ${points[i].x} ${points[i].y} ${cx} ${cy}`;
  }
  const last = points[points.length - 1];
  d += ` L ${last.x} ${last.y}`;
  return d;
}

// Compose a pen-tool path from a list of anchor points (straight or bezier).
export function penPath(points, closed = false) {
  if (!points.length) return "";
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    d += ` L ${points[i].x} ${points[i].y}`;
  }
  if (closed) d += " Z";
  return d;
}

// Free-draw hook: returns handler and a preview path string while drawing.
export function useFreeDraw({ tool, getSvgPoint, onCommit, strokeColor, strokeWidth, fill }) {
  const [livePath, setLivePath] = useState("");
  const pointsRef = useRef([]);
  const activeRef = useRef(false);

  const onPointerDown = useCallback(
    (e) => {
      if (tool !== "pencil") return false;
      e.stopPropagation();
      const p = getSvgPoint(e.clientX, e.clientY);
      pointsRef.current = [p];
      activeRef.current = true;
      setLivePath(`M ${p.x} ${p.y}`);
      e.currentTarget.setPointerCapture?.(e.pointerId);
      return true;
    },
    [tool, getSvgPoint]
  );

  const onPointerMove = useCallback(
    (e) => {
      if (!activeRef.current) return;
      const p = getSvgPoint(e.clientX, e.clientY);
      const last = pointsRef.current[pointsRef.current.length - 1];
      const dx = p.x - last.x;
      const dy = p.y - last.y;
      if (dx * dx + dy * dy < SIMPLIFY_THRESHOLD * SIMPLIFY_THRESHOLD) return;
      pointsRef.current.push(p);
      setLivePath(smoothPath(pointsRef.current));
    },
    [getSvgPoint]
  );

  const onPointerUp = useCallback(() => {
    if (!activeRef.current) return;
    activeRef.current = false;
    const pts = pointsRef.current;
    if (pts.length >= 2) {
      const d = smoothPath(pts);
      onCommit({
        id: nanoIdLike(),
        type: "path",
        attrs: {
          d,
          fill: fill || "none",
          stroke: strokeColor || "#FF6B00",
          strokeWidth: strokeWidth || 3,
          strokeLinecap: "round",
          strokeLinejoin: "round",
          opacity: 1,
        },
        animation: drawAnimation(),
      });
    }
    pointsRef.current = [];
    setLivePath("");
  }, [onCommit, strokeColor, strokeWidth, fill]);

  return { onPointerDown, onPointerMove, onPointerUp, livePath, isActive: activeRef.current };
}

// ===== Drag-to-draw basic shapes (rect/circle/line/triangle/star/text) =====
// Selecting a shape tool records the shape type; the user then presses and
// drags on the canvas to size the shape with a live, reduced-opacity preview,
// and releasing commits it.
const SHAPE_TOOLS = ["rect", "circle", "line", "polygon", "star", "arrow"];

// Irregular shapes that are drawn from their CENTER outward: the press point
// `a` is pinned as the shape's center and the drag distance becomes the
// radius (half-extent on each axis). This eliminates the "drift" the old
// bounding-box model produced, where the press point was a bbox corner and the
// shape's visual center slid toward the cursor while dragging. Rect and line
// keep the corner / endpoint model unchanged.
const CENTER_DRAW_TOOLS = ["circle", "polygon", "star"];

export function isCenterDrawTool(tool) {
  return CENTER_DRAW_TOOLS.includes(tool);
}

// Unified default color for newly drawn elements. Change this single value to
// re-theme the default fill (shapes) and stroke (lines) across the editor.
export const DEFAULT_ELEMENT_COLOR = "#002FA7";

const shapeBaseAttr = (overrides) => ({
  fill: DEFAULT_ELEMENT_COLOR,
  stroke: "#0a0a0a",
  strokeWidth: 0,
  opacity: 1,
  ...overrides,
});

// Build a shape element from a drag from point `a` to point `b`.
// - rect / text: `a` is a bounding-box corner, `b` the opposite corner.
// - line: `a`/`b` are the two endpoints.
// - circle / polygon / star (irregular): `a` is the FIXED center and the drag
//   grows the radius outward (|dx| on x, |dy| on y), so the start point never
//   drifts.
export function buildShapeFromDrag(tool, a, b) {
  // Center-anchored model for irregular shapes.
  const rx = Math.abs(b.x - a.x);
  const ry = Math.abs(b.y - a.y);
  const cx = a.x;
  const cy = a.y;
  // Bounding-box model for rect/text.
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const w = Math.abs(b.x - a.x);
  const h = Math.abs(b.y - a.y);
  switch (tool) {
    case "rect":
      return { id: nanoIdLike(), type: "rect", attrs: shapeBaseAttr({ x, y, width: w, height: h, rx: 0 }), animation: null };
    case "circle":
      // Ellipse centered on the press point (a perfect circle on a uniform
      // drag); dragging out in any direction expands the radius from center.
      return { id: nanoIdLike(), type: "ellipse", attrs: shapeBaseAttr({ cx, cy, rx, ry }), animation: null };
    case "line":
      return { id: nanoIdLike(), type: "line", attrs: shapeBaseAttr({ x1: a.x, y1: a.y, x2: b.x, y2: b.y, stroke: DEFAULT_ELEMENT_COLOR, strokeWidth: 3, fill: "none" }), animation: null };
    case "arrow":
      // An arrow is a <path> whose `d` contains the main stroke plus endpoint
      // decorations (start + end) as extra sub-paths. Because the decorations
      // are part of the path, draw animations (stroke-dashoffset) reveal them
      // naturally as the stroke reaches each endpoint. The `arrow: true` flag
      // is kept so line-edit (fold / arc) re-appends the decorations on
      // serialize. `arrowStart` / `arrowEnd` select the decoration type for
      // each endpoint. `lineEdit: true` marks it as an editable path.
      return {
        id: nanoIdLike(),
        type: "path",
        attrs: shapeBaseAttr({
          d: `M ${a.x} ${a.y} L ${b.x} ${b.y}` + arrowHeadPath([{ x: a.x, y: a.y }, { x: b.x, y: b.y }], 3),
          stroke: DEFAULT_ELEMENT_COLOR,
          strokeWidth: 3,
          fill: "none",
          arrow: true,
          arrowStart: "none",
          arrowEnd: "line-arrow",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          lineEdit: true,
        }),
        animation: null,
      };
    case "polygon":
      // Triangle centered on the press point, growing outward by radius.
      return { id: nanoIdLike(), type: "polygon", attrs: shapeBaseAttr({ points: `${cx},${cy - ry} ${cx - rx},${cy + ry} ${cx + rx},${cy + ry}` }), animation: null };
    case "star": {
      // Star centered on the press point; radius grows outward from center.
      const inner = 0.42;
      const pts = [];
      for (let i = 0; i < 10; i++) {
        const ang = (Math.PI / 5) * i - Math.PI / 2;
        const f = i % 2 === 0 ? 1 : inner;
        pts.push(`${(cx + rx * f * Math.cos(ang)).toFixed(1)},${(cy + ry * f * Math.sin(ang)).toFixed(1)}`);
      }
      return { id: nanoIdLike(), type: "polygon", attrs: shapeBaseAttr({ points: pts.join(" "), fill: "#FFB400" }), animation: null };
    }
    case "text":
      return { id: nanoIdLike(), type: "text", attrs: { x, y: y + Math.max(h, 32), fill: "#0a0a0a", fontSize: 32, fontFamily: "IBM Plex Sans", text: "Hello SVG" }, animation: null };
    default:
      return null;
  }
}

// Shape-draw hook: returns pointer handlers + a live preview element while
// dragging. A click without a meaningful drag falls back to a default size so
// a single click still produces a usable shape.
export function useShapeDraw({ tool, getSvgPoint, onCommit }) {
  const [preview, setPreview] = useState(null);
  const startRef = useRef(null);
  const activeRef = useRef(false);
  const isShapeTool = SHAPE_TOOLS.includes(tool);

  const onPointerDown = useCallback(
    (e) => {
      if (!isShapeTool) return false;
      e.stopPropagation();
      const p = getSvgPoint(e.clientX, e.clientY);
      startRef.current = p;
      activeRef.current = true;
      setPreview(buildShapeFromDrag(tool, p, { x: p.x + 1, y: p.y + 1 }));
      e.currentTarget.setPointerCapture?.(e.pointerId);
      return true;
    },
    [isShapeTool, tool, getSvgPoint]
  );

  const onPointerMove = useCallback(
    (e) => {
      if (!activeRef.current) return;
      const p = getSvgPoint(e.clientX, e.clientY);
      setPreview(buildShapeFromDrag(tool, startRef.current, p));
    },
    [tool, getSvgPoint]
  );

  const onPointerUp = useCallback(
    (e) => {
      if (!activeRef.current) return;
      activeRef.current = false;
      const start = startRef.current;
      const p = getSvgPoint(e.clientX, e.clientY);
      startRef.current = null;
      setPreview(null);
      let b = p;
      if (Math.abs(p.x - start.x) < 4 && Math.abs(p.y - start.y) < 4) {
        // Click without a meaningful drag: produce a default-sized shape.
        // Center-draw shapes grow from the center, so a half-extent offset
        // keeps the shape centered on the click point; rect/line keep the
        // corner / endpoint default.
        if (tool === "line" || tool === "arrow") b = { x: start.x + 160, y: start.y };
        else if (isCenterDrawTool(tool)) b = { x: start.x + 60, y: start.y + 60 };
        else b = { x: start.x + 120, y: start.y + 80 };
      }
      const el = buildShapeFromDrag(tool, start, b);
      if (el) onCommit(el);
    },
    [tool, getSvgPoint, onCommit]
  );

  return { onPointerDown, onPointerMove, onPointerUp, preview, isShapeTool };
}
export function usePenTool({ tool, getSvgPoint, onCommit, strokeColor, strokeWidth, fill }) {
  const [points, setPoints] = useState([]);
  const [hover, setHover] = useState(null);

  const commitNow = useCallback(
    (closed) => {
      if (points.length < 2) {
        setPoints([]);
        return;
      }
      const d = penPath(points, closed);
      onCommit({
        id: nanoIdLike(),
        type: "path",
        attrs: {
          d,
          fill: closed ? fill || "#FF6B00" : "none",
          stroke: strokeColor || "#FF6B00",
          strokeWidth: strokeWidth || 3,
          strokeLinecap: "round",
          strokeLinejoin: "round",
          opacity: 1,
        },
        animation: drawAnimation(),
      });
      setPoints([]);
      setHover(null);
    },
    [points, onCommit, strokeColor, strokeWidth, fill]
  );

  // Reset on tool deactivation.
  useEffect(() => {
    if (tool !== "pen" && points.length) {
      setPoints([]);
      setHover(null);
    }
  }, [tool, points.length]);

  useEffect(() => {
    if (tool !== "pen") return undefined;
    const onKey = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commitNow(false);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setPoints([]);
        setHover(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tool, commitNow]);

  const onPointerDown = useCallback(
    (e) => {
      if (tool !== "pen") return false;
      e.stopPropagation();
      const p = getSvgPoint(e.clientX, e.clientY);
      // Close-path click on first point.
      if (points.length >= 3) {
        const first = points[0];
        const dx = p.x - first.x;
        const dy = p.y - first.y;
        if (Math.sqrt(dx * dx + dy * dy) < 8) {
          commitNow(true);
          return true;
        }
      }
      setPoints((prev) => [...prev, p]);
      return true;
    },
    [tool, getSvgPoint, points, commitNow]
  );

  const onPointerMove = useCallback(
    (e) => {
      if (tool !== "pen") return;
      setHover(getSvgPoint(e.clientX, e.clientY));
    },
    [tool, getSvgPoint]
  );

  const onDoubleClick = useCallback(
    (e) => {
      if (tool !== "pen") return;
      e.stopPropagation();
      commitNow(false);
    },
    [tool, commitNow]
  );

  const livePath = points.length
    ? penPath([...points, ...(hover ? [hover] : [])])
    : "";

  return { onPointerDown, onPointerMove, onDoubleClick, livePath, points };
}
